import React from 'react';
import { styled } from '@compiled/react';
import { Button, LoadingButton } from '@helpcenter/analytics-components';
import type { ButtonProps, LoadingButtonProps } from '@helpcenter/analytics-components';
import FeedbackCollectorButton from '@helpcenter/feedback-collector-button';
import * as zIndex from '@helpcenter/styles/src/z-index';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { BrandingPreviewBanner } from 'view/advanced-help-center-branding/branding-preview-banner';
import InlineMessage from '@atlaskit/inline-message';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import messages from './messages';

export interface ActionBarStatelessProps {
    isPageInBrandingPreviewMode?: boolean;
    isPublishing?: boolean;
    onPublish?: () => void;
    onClose?: () => void;
    publishButtonDisabled?: boolean;
    previewButtonDisabled?: boolean;
    children?: React.ReactNode;
    isPageInPreviewMode?: boolean;
    togglePreviewMode?: () => void;
}

export const HELP_CENTERS_CUSTOMIZE_PAGE_LAYOUT = 'Help Centers customize page layout';

export const ActionBarStateless: React.FC<ActionBarStatelessProps> = ({
    isPageInBrandingPreviewMode,
    isPublishing,
    onPublish,
    onClose,
    publishButtonDisabled,
    previewButtonDisabled,
    children,
    isPageInPreviewMode,
    togglePreviewMode,
}) => {
    di(BrandingPreviewBanner);
    const { formatMessage } = useIntl();

    if (isPageInBrandingPreviewMode) {
        return (
            <ActionsWrapper isPageInBrandingPreviewMode={isPageInBrandingPreviewMode}>
                <BrandingPreviewBanner />
            </ActionsWrapper>
        );
    }

    return (
        <ActionsWrapper
            id="advance-customization-action-bar"
            isPageInBrandingPreviewMode={!!isPageInBrandingPreviewMode}
        >
            <ButtonContainer>
                {!isPageInPreviewMode && (
                    <Box xcss={buttonGroup}>
                        <ButtonWrapper actionSubjectId="backToHomePage" appearance="subtle" onClick={onClose}>
                            {formatMessage(messages.backToSettings)}
                        </ButtonWrapper>
                        <FeedbackCollectorButton
                            showIcon
                            showTypeField
                            featureName={HELP_CENTERS_CUSTOMIZE_PAGE_LAYOUT}
                            buttonLabel={messages.giveFeedback}
                            analyticsSubjectId="customizePageLayoutFeedbackButton"
                        />
                    </Box>
                )}

                {isPageInPreviewMode && (
                    <PreviewButtonWrapper>
                        <InlineMessage appearance="info" title={formatMessage(messages.previewMessage)} />
                    </PreviewButtonWrapper>
                )}
                <ButtonGroup>
                    <Button
                        isDisabled={previewButtonDisabled}
                        actionSubjectId="preview"
                        appearance="default"
                        onClick={togglePreviewMode}
                    >
                        {isPageInPreviewMode ? formatMessage(messages.exitPreview) : formatMessage(messages.preview)}
                    </Button>
                    <ActionButton
                        appearance="primary"
                        actionSubjectId="publish"
                        isDisabled={publishButtonDisabled}
                        onClick={onPublish}
                        isLoading={isPublishing}
                    >
                        {formatMessage(messages.publish)}
                    </ActionButton>
                </ButtonGroup>
            </ButtonContainer>
            {children}
        </ActionsWrapper>
    );
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const ButtonWrapper = styled<Omit<ButtonProps, 'theme'>>(Button)({
    marginLeft: token('space.negative.150', '-12px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const ButtonGroup = styled.div({
    position: 'relative',
});

const buttonGroup = xcss({
    display: 'flex',
    flexDirection: 'row',
    gap: 'space.050',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const ActionButton = styled<Omit<LoadingButtonProps, 'theme'>>(LoadingButton)({
    marginLeft: token('space.050', '4px'),
});

interface ActionsWrapperProps {
    isPageInBrandingPreviewMode: boolean;
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const ActionsWrapper = styled.div<ActionsWrapperProps>({
    // Upgrading @atlaskit/platform; fix these violations.
    // eslint-disable-next-line @atlaskit/platform/expand-spacing-shorthand
    padding: `${token('space.150', '12px')} ${token('space.300', '24px')}`,
    top: 0,
    left: 0,
    width: '100%',
    position: 'sticky',
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Disabled to rollout go/ui-styling-standard tooling, please resolve
    backgroundColor: (props) =>
        props.isPageInBrandingPreviewMode
            ? token('color.background.accent.gray.bolder', '#44546F')
            : token('elevation.surface', '#FFFFFF'),
    boxShadow: token(
        'elevation.shadow.overlay',
        ' 0px 0px 8px 0px rgba(9, 30, 66, 0.16), 0px 0px 1px 0px rgba(9, 30, 66, 0.12)'
    ),
    boxSizing: 'border-box',
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Disabled to rollout go/ui-styling-standard tooling, please resolve
    zIndex: zIndex.modal + 1, // plus 1 because toolbar of rich text editor comes on top
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const ButtonContainer = styled.div({
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const PreviewButtonWrapper = styled.div({
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    pointerEvents: 'none',
});
